import React, { useState, useEffect } from "react";  
import { useNavigate } from "react-router-dom";  
import api from "../api";  
import Navbar from "./Navbar";  
import { motion } from "framer-motion";  
  
const Accounts = () => {  
  const navigate = useNavigate();  
  const [accounts, setAccounts] = useState({ binance: null, ig: null });  
  const [loading, setLoading] = useState(true);  
  
  useEffect(() => {  
    fetchAccounts();  
  }, []);  
  
  const fetchAccounts = async () => {  
    try {  
      const [statusRes, igRes] = await Promise.all([  
        api.get("/config/status"),  
        api.get("/users/ig/status"),  
      ]);  
  
      setAccounts({  
        binance: statusRes.data.has_binance_keys  
          ? { connected: true, testnet: statusRes.data.testnet || false }  
          : null,  
        ig: igRes.data.connected  
          ? {  
              connected: true,  
              account_type: igRes.data.account_type,  
              ig_username: igRes.data.username,  
            }  
          : null,  
      });  
    } catch (error) {  
      console.error("Failed to fetch accounts:", error);  
    } finally {  
      setLoading(false);  
    }  
  };  
  
  const connectedCount = (accounts.binance ? 1 : 0) + (accounts.ig ? 1 : 0);  
  
  if (loading) {  
    return (  
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">  
        <Navbar />  
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">  
          <div className="text-center">  
            <div className="relative w-14 h-14 mx-auto mb-4">  
              <div className="absolute inset-0 border-4 border-gold/20 rounded-full" />  
              <div className="absolute inset-0 border-4 border-gold border-t-transparent rounded-full animate-spin" />  
            </div>  
            <p className="text-gold text-sm font-semibold">Loading accounts…</p>  
          </div>  
        </div>  
      </div>  
    );  
  }  
  
  return (  
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">  
      <Navbar />  
  
      <div className="max-w-6xl mx-auto py-6 sm:py-10 px-4 sm:px-6 lg:px-8">  
        {/* Page Header */}  
        <motion.div  
          initial={{ opacity: 0, y: 12 }}  
          animate={{ opacity: 1, y: 0 }}  
          className="mb-8 sm:mb-10"  
        >  
          <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">  
            <div className="flex items-center gap-3">  
              <div className="w-1 h-10 bg-gradient-to-b from-gold to-yellow-600 rounded-full" />  
              <div>  
                <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">  
                  My Accounts  
                </h1>  
                <p className="text-gray-400 text-sm mt-1">  
                  Connect a broker to let the bot trade on your behalf  
                </p>  
              </div>  
            </div>  
  
            <div className="flex items-center gap-2 px-4 py-2 bg-black/40 border border-gold/20 rounded-full backdrop-blur-sm">  
              <span className={`w-2 h-2 rounded-full ${connectedCount > 0 ? "bg-green-400 animate-pulse" : "bg-gray-600"}`} />  
              <span className="text-xs font-semibold text-gray-300">  
                {connectedCount} of 2 connected  
              </span>  
            </div>  
          </div>  
        </motion.div>  
  
        {/* Cards */}  
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">  
          <motion.div  
            initial={{ opacity: 0, y: 12 }}  
            animate={{ opacity: 1, y: 0 }}  
            transition={{ delay: 0.05 }}  
          >  
            <AccountCard  
              title="Binance"  
              purpose="Bitcoin & crypto trading"  
              icon={  
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">  
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />  
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />  
                </svg>  
              }  
              accentColor="orange"  
              connected={!!accounts.binance}  
              details={  
                accounts.binance  
                  ? [{ label: "Mode", value: accounts.binance.testnet ? "Testnet (Demo)" : "Live" }]  
                  : []  
              }  
              onConnect={() => navigate("/add-api")}  
              onUpdate={() => navigate("/add-api")}  
            />  
          </motion.div>  
  
          <motion.div  
            initial={{ opacity: 0, y: 12 }}  
            animate={{ opacity: 1, y: 0 }}  
            transition={{ delay: 0.1 }}  
          >  
            <AccountCard  
              title="IG Markets"  
              purpose="Gold & CFD trading"  
              icon={  
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">  
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />  
                </svg>  
              }  
              accentColor="yellow"  
              connected={!!accounts.ig}  
              details={  
                accounts.ig  
                  ? [  
                      { label: "Account Type", value: accounts.ig.account_type || "—" },  
                      { label: "Username", value: accounts.ig.ig_username || "—" },  
                    ]  
                  : []  
              }  
              onConnect={() => navigate("/add-ig")}  
              onUpdate={() => navigate("/add-ig")}  
            />  
          </motion.div>  
        </div>  
  
        {/* Security footer */}  
        <motion.div  
          initial={{ opacity: 0 }}  
          animate={{ opacity: 1 }}  
          transition={{ delay: 0.2 }}  
          className="bg-black/30 border border-gray-800 rounded-xl p-5 backdrop-blur-sm"  
        >  
          <div className="flex items-start gap-3">  
            <div className="w-8 h-8 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-center flex-shrink-0">  
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">  
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />  
              </svg>  
            </div>  
            <div className="flex-1">  
              <h4 className="text-sm font-semibold text-white mb-1">Your keys are safe</h4>  
              <p className="text-xs text-gray-400 leading-relaxed">  
                API keys are encrypted at rest and only used to place trades on your behalf. We never store passwords, cannot withdraw your funds, and you can disconnect at any time.  
              </p>  
            </div>  
          </div>  
        </motion.div>  
      </div>  
    </div>  
  );  
};  
  
/* ─── Accent styling ─── */  
const ACCENT_STYLES = {  
  orange: { iconBg: "bg-orange-500/10 border-orange-500/30", text: "text-orange-400" },  
  yellow: { iconBg: "bg-yellow-500/10 border-yellow-500/30", text: "text-yellow-400" },  
};  
  
/* ─── Account Card ─── */  
const AccountCard = ({  
  title,  
  purpose,  
  icon,  
  accentColor,  
  connected,  
  details,  
  onConnect,  
  onUpdate,  
}) => {  
  const accent = ACCENT_STYLES[accentColor] || ACCENT_STYLES.orange;  
  
  return (  
    <div  
      className={`  
        relative h-full flex flex-col p-6  
        bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60  
        backdrop-blur-xl rounded-2xl border transition-all duration-300  
        ${connected  
          ? "border-green-500/40 shadow-lg shadow-green-500/10"  
          : "border-gray-800 hover:border-gold/30"}  
      `}  
    >  
      {/* Header */}  
      <div className="flex items-start justify-between mb-6">  
        <div className="flex items-center gap-3 min-w-0">  
          <div className={`w-11 h-11 border rounded-xl flex items-center justify-center flex-shrink-0 ${accent.iconBg} ${accent.text}`}>  
            {icon}  
          </div>  
          <div className="min-w-0">  
            <h3 className="text-lg font-bold text-white leading-tight truncate">{title}</h3>  
            <p className="text-xs text-gray-500 mt-0.5 truncate">{purpose}</p>  
          </div>  
        </div>  
  
        {connected && (  
          <span className="flex items-center gap-1.5 px-2 py-1 bg-green-500/10 border border-green-500/40 rounded-full flex-shrink-0">  
            <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />  
            <span className="text-[10px] font-bold uppercase tracking-wider text-green-400">  
              Connected  
            </span>  
          </span>  
        )}  
      </div>  
  
      {/* Body */}  
      {connected ? (  
        <>  
          <div className="space-y-2 mb-6 flex-1">  
            {details.map((d, i) => (  
              <div  
                key={i}  
                className="flex items-center justify-between px-3 py-2.5 bg-black/40 border border-gray-800 rounded-lg"  
              >  
                <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">  
                  {d.label}  
                </span>  
                <span className="text-sm font-semibold text-white truncate ml-3">  
                  {d.value}  
                </span>  
              </div>  
            ))}  
          </div>  
          <button  
            onClick={onUpdate}  
            className="w-full py-2.5 px-4 bg-white/[0.03] hover:bg-white/[0.06] border border-gray-800 hover:border-gray-700 rounded-xl text-gray-300 hover:text-white font-semibold text-sm transition-all flex items-center justify-center gap-2"  
          >  
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">  
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />  
            </svg>  
            Update credentials  
          </button>  
        </>  
      ) : (  
        <>  
          <ul className="space-y-2.5 mb-6 flex-1">  
            <BenefitRow>Read market data & place trades securely</BenefitRow>  
            <BenefitRow>Bank-grade AES encryption on all keys</BenefitRow>  
            <BenefitRow>Disconnect anytime — you stay in control</BenefitRow>  
          </ul>  
          <button  
            onClick={onConnect}  
            className="w-full py-3 px-4 rounded-xl font-bold text-sm bg-gradient-to-r from-gold to-yellow-600 text-black hover:from-yellow-500 hover:to-gold transition-all shadow-lg shadow-gold/20 flex items-center justify-center gap-2"  
          >  
            Connect {title}  
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />  
            </svg>  
          </button>  
        </>  
      )}  
    </div>  
  );  
};  
  
const BenefitRow = ({ children }) => (  
  <li className="flex items-start gap-2.5 text-xs text-gray-400">  
    <svg className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">  
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />  
    </svg>  
    <span className="leading-relaxed">{children}</span>  
  </li>  
);  
  
export default Accounts;  